import { zipSync } from "fflate";

const textEncoder = new TextEncoder();

function base64ToBytes(base64) {
  const clean = String(base64 || "").trim().replace(/^data:[^,]+,/i, "").replace(/\s/g, "");
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function concatArrays(chunks) {
  const length = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

export function normalizeZipPath(path, fallback) {
  const clean = String(path || fallback || "asset.txt")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/\.\.+/g, ".")
    .replace(/[^\w./ ()@+-]/g, "-");
  return clean || fallback || "asset.txt";
}

export function uniquePath(path, seen) {
  if (!seen.has(path)) {
    seen.add(path);
    return path;
  }

  const dot = path.lastIndexOf(".");
  const base = dot > 0 ? path.slice(0, dot) : path;
  const ext = dot > 0 ? path.slice(dot) : "";
  let index = 2;
  let candidate = `${base}-${index}${ext}`;
  while (seen.has(candidate)) {
    index += 1;
    candidate = `${base}-${index}${ext}`;
  }
  seen.add(candidate);
  return candidate;
}

function createZip(files) {
  const zipInput = {};
  for (const file of files) {
    zipInput[file.path] = file.bytes;
  }
  return new Blob([zipSync(zipInput, { level: 0 })], { type: "application/zip" });
}

export async function readFilesAsAssetEntries(files) {
  const list = Array.from(files || []);
  return Promise.all(
    list.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = String(reader.result || "");
            const data = result.includes(",") ? result.slice(result.indexOf(",") + 1) : result;
            resolve({
              fileName: file.name,
              mimeType: file.type || "application/octet-stream",
              size: file.size,
              data,
            });
          };
          reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
          reader.readAsDataURL(file);
        })
    )
  );
}

function assetToBytes(asset) {
  if (asset?.data) return base64ToBytes(asset.data);
  return textEncoder.encode(String(asset?.content || ""));
}

function remoteAssetUrl(asset) {
  return asset?.sourceUrl || asset?.sourceDownloadUrl || asset?.sourcePresignedUrl || "";
}

function hasRemoteAssetSource(asset) {
  return Boolean(asset?.chunkSourceUrl || remoteAssetUrl(asset));
}

function remoteAssetSize(asset) {
  const size = Number(asset?.size ?? asset?.contentLength ?? asset?.byteLength);
  return Number.isFinite(size) && size >= 0 ? size : undefined;
}

const REMOTE_ASSET_CHUNK_BYTES = 2 * 1024 * 1024;

const PREVIEW_TEXT_MAX = 256 * 1024;

function previewBytesAsTextOrBinary(bytes) {
  if (!bytes?.length) return { kind: "text", text: "", size: 0 };
  const truncated = bytes.length > PREVIEW_TEXT_MAX;
  const slice = truncated ? bytes.slice(0, PREVIEW_TEXT_MAX) : bytes;
  const decoder = new TextDecoder("utf-8", { fatal: false });
  const text = decoder.decode(slice);
  const replacementCount = (text.match(/\uFFFD/g) || []).length;
  if (replacementCount > Math.min(200, slice.length * 0.03)) {
    return { kind: "binary", size: bytes.length };
  }
  return {
    kind: "text",
    text: truncated
      ? `${text}\n\n… Preview truncated (${bytes.length.toLocaleString()} bytes total)`
      : text,
    size: bytes.length,
  };
}

/**
 * Virtual listing of files that would appear in the downloadable ZIP (matches downloadAssetBundleZip paths).
 */
export function buildVirtualBundleZipEntries({ bundle, pdfAttachment, uploadedAssets }) {
  const seen = new Set();
  const entries = [];

  const directAsset =
    (bundle?.downloadMode === "direct" || bundle?.directDownload) &&
    Array.isArray(bundle?.assets) &&
    bundle.assets.length === 1
      ? bundle.assets[0]
      : null;

  if (directAsset) {
    const path = uniquePath(normalizeZipPath(directAsset.filename || bundle?.fileName, "download"), seen);
    if (hasRemoteAssetSource(directAsset) && !directAsset.data) {
      entries.push({
        path,
        kind: "binary",
        size: remoteAssetSize(directAsset),
        hint: "Video - included when you download",
      });
      return entries.sort((a, b) => a.path.localeCompare(b.path));
    }
    const bytes = assetToBytes(directAsset);
    let row;
    if (directAsset.content != null && directAsset.data == null) {
      row = { path, kind: "text", text: String(directAsset.content), size: bytes.length };
    } else {
      row = { path, ...previewBytesAsTextOrBinary(bytes) };
    }
    entries.push(row);
    return entries.sort((a, b) => a.path.localeCompare(b.path));
  }

  for (const asset of Array.isArray(bundle?.assets) ? bundle.assets : []) {
    const path = uniquePath(normalizeZipPath(asset.filename, "asset.txt"), seen);
    if (hasRemoteAssetSource(asset) && !asset.data) {
      entries.push({
        path,
        kind: "binary",
        size: remoteAssetSize(asset),
        hint: "Video - included when you download",
      });
      continue;
    }
    const bytes = assetToBytes(asset);
    let row;
    if (asset.content != null && asset.data == null) {
      row = { path, kind: "text", text: String(asset.content), size: bytes.length };
    } else {
      row = { path, ...previewBytesAsTextOrBinary(bytes) };
    }
    entries.push(row);
  }

  if (pdfAttachment?.data) {
    const path = uniquePath(
      normalizeZipPath(`campaign-summary/${pdfAttachment.filename || "campaign-summary.pdf"}`),
      seen
    );
    entries.push({
      path,
      kind: "binary",
      size: base64ToBytes(pdfAttachment.data).length,
      hint: "PDF — included in the ZIP download",
      bundledAssistantPdf: true,
    });
  }

  for (const uploaded of Array.isArray(uploadedAssets) ? uploadedAssets : []) {
    if (!uploaded?.data) continue;
    const path = uniquePath(normalizeZipPath(`uploaded-source/${uploaded.fileName || "uploaded-file"}`), seen);
    const bytes = base64ToBytes(uploaded.data);
    entries.push({ path, ...previewBytesAsTextOrBinary(bytes) });
  }

  return entries.sort((a, b) => a.path.localeCompare(b.path));
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function fetchRemoteUrlBytes(url) {
  const res = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!res.ok) {
    throw new Error(`Asset fetch failed (${res.status})`);
  }
  return new Uint8Array(await res.arrayBuffer());
}

function parseRemoteAssetSize(value) {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : undefined;
}

async function fetchRemoteChunk(asset, offset, end) {
  const url = new URL(asset.chunkSourceUrl, window.location.href);
  url.searchParams.set("start", String(offset));
  url.searchParams.set("end", String(end));

  const res = await fetch(url.toString(), { mode: "cors", credentials: "omit" });
  if (!res.ok) {
    throw new Error(`Asset fetch failed (${res.status})`);
  }

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.toLowerCase().includes("application/json")) {
    const bytes = new Uint8Array(await res.arrayBuffer());
    return {
      bytes,
      done: bytes.length < end - offset + 1,
    };
  }

  const payload = await res.json();
  if (payload?.error) {
    throw new Error(String(payload.error));
  }
  if (payload?.encoding !== "base64" || typeof payload?.data !== "string") {
    throw new Error("Asset fetch failed (invalid chunk payload)");
  }

  const bytes = base64ToBytes(payload.data);
  const payloadStart = Number(payload.start);
  const payloadEnd = Number(payload.end);
  if (Number.isFinite(payloadStart) && payloadStart !== offset) {
    throw new Error(`Asset fetch failed (chunk start mismatch: expected ${offset}, got ${payloadStart})`);
  }
  if (Number.isFinite(payloadEnd) && payloadEnd !== offset + bytes.length - 1) {
    throw new Error("Asset fetch failed (chunk end mismatch)");
  }

  return {
    bytes,
    done: Boolean(payload.done),
    size: parseRemoteAssetSize(payload.size),
  };
}

function validatePlayableMp4Bytes(asset, bytes) {
  const path = String(asset?.filename || "").toLowerCase();
  const mimeType = String(asset?.mimeType || "").toLowerCase();
  if (!path.endsWith(".mp4") && mimeType !== "video/mp4") return;
  const limit = Math.min(bytes.length - 3, 4096);
  for (let i = 0; i < limit; i += 1) {
    if (
      bytes[i] === 0x66 &&
      bytes[i + 1] === 0x74 &&
      bytes[i + 2] === 0x79 &&
      bytes[i + 3] === 0x70
    ) {
      return;
    }
  }
  throw new Error("Asset fetch failed (downloaded video bytes do not look like an MP4)");
}

async function fetchRemoteAssetBytesChunked(asset, size) {
  const chunks = [];
  let offset = 0;
  let resolvedSize = size;
  let done = false;

  while (!done && (!resolvedSize || offset < resolvedSize)) {
    const chunkSize = parseRemoteAssetSize(asset?.chunkSize) || REMOTE_ASSET_CHUNK_BYTES;
    const end = resolvedSize
      ? Math.min(offset + chunkSize - 1, resolvedSize - 1)
      : offset + chunkSize - 1;
    const chunk = await fetchRemoteChunk(asset, offset, end);
    const bytes = chunk.bytes;
    if (!bytes.length) {
      throw new Error("Asset fetch failed (empty chunk)");
    }
    chunks.push(bytes);
    resolvedSize = resolvedSize || chunk.size;
    offset += bytes.length;
    done = chunk.done || (resolvedSize ? offset >= resolvedSize : false);
  }

  const out = concatArrays(chunks);
  if (resolvedSize && out.length !== resolvedSize) {
    throw new Error(
      `Asset fetch failed (chunked size mismatch: expected ${resolvedSize} bytes, got ${out.length})`
    );
  }
  validatePlayableMp4Bytes(asset, out);
  return out;
}

/**
 * Prefer same-origin chunked API download when available (reliable CORS vs raw S3 presigned GET).
 * Fall back to presigned URL when chunked fails or total length does not match HEAD size.
 */
async function fetchRemoteAssetBytes(asset) {
  const directUrl = remoteAssetUrl(asset);
  const size = remoteAssetSize(asset);
  const canChunk = Boolean(asset?.chunkSourceUrl);

  if (canChunk) {
    try {
      return await fetchRemoteAssetBytesChunked(asset, size);
    } catch (chunkErr) {
      if (directUrl) {
        console.warn(
          "[assetBundleZip] Chunked download failed; falling back to presigned URL",
          chunkErr
        );
        try {
          return await fetchRemoteUrlBytes(directUrl);
        } catch (urlErr) {
          console.error("[assetBundleZip] Presigned URL fallback failed", urlErr);
          throw chunkErr;
        }
      }
      throw chunkErr;
    }
  }

  if (directUrl) {
    const bytes = await fetchRemoteUrlBytes(directUrl);
    const expectedSize = remoteAssetSize(asset);
    if (expectedSize && bytes.length !== expectedSize) {
      throw new Error(
        `Asset fetch failed (size mismatch: expected ${expectedSize} bytes, got ${bytes.length})`
      );
    }
    validatePlayableMp4Bytes(asset, bytes);
    return bytes;
  }

  throw new Error("Asset fetch failed (missing URL)");
}

export async function downloadAssetBundleZip({ bundle, pdfAttachment, uploadedAssets }) {
  const directAsset =
    (bundle?.downloadMode === "direct" || bundle?.directDownload) &&
    Array.isArray(bundle?.assets) &&
    bundle.assets.length === 1
      ? bundle.assets[0]
      : null;

  if (directAsset) {
    const filename = normalizeZipPath(
      directAsset.filename || bundle?.fileName,
      "download"
    );
    if (hasRemoteAssetSource(directAsset) && !directAsset.data) {
      const bytes = await fetchRemoteAssetBytes(directAsset);
      const blob = new Blob([bytes], {
        type: directAsset.mimeType || bundle?.mimeType || "application/octet-stream",
      });
      downloadBlob(blob, filename);
      return;
    }
    const blob = new Blob([assetToBytes(directAsset)], {
      type:
        directAsset.mimeType ||
        bundle?.mimeType ||
        "application/octet-stream",
    });
    downloadBlob(blob, filename);
    return;
  }

  const seen = new Set();
  const files = [];

  for (const asset of Array.isArray(bundle?.assets) ? bundle.assets : []) {
    const zipPath = uniquePath(normalizeZipPath(asset.filename, "asset.txt"), seen);
    if (hasRemoteAssetSource(asset) && !asset.data) {
      const bytes = await fetchRemoteAssetBytes(asset);
      files.push({ path: zipPath, bytes });
      continue;
    }
    files.push({
      path: zipPath,
      bytes: assetToBytes(asset),
    });
  }

  if (pdfAttachment?.data) {
    files.push({
      path: uniquePath(normalizeZipPath(`campaign-summary/${pdfAttachment.filename || "campaign-summary.pdf"}`), seen),
      bytes: base64ToBytes(pdfAttachment.data),
    });
  }

  for (const uploaded of Array.isArray(uploadedAssets) ? uploadedAssets : []) {
    if (!uploaded?.data) continue;
    files.push({
      path: uniquePath(normalizeZipPath(`uploaded-source/${uploaded.fileName || "uploaded-file"}`), seen),
      bytes: base64ToBytes(uploaded.data),
    });
  }

  if (!files.length) return;

  const blob = createZip(files);
  downloadBlob(
    blob,
    normalizeZipPath(bundle?.fileName || "Campaign-Assets.zip", "Campaign-Assets.zip")
  );
}
